import { memo, useEffect, useMemo, useRef } from 'react'
import {
  Animated,
  Easing,
  Platform,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native'
import { SvgRect } from './svg/SvgRect'
import { SvgStar } from './svg/SvgStar'
import { SvgTriangle } from './svg/SvgTriangle'
import { SvgHex } from './svg/SvgHex'

const SHAPES = [SvgRect, SvgStar, SvgTriangle, SvgHex]
const COLORS = ['#F8CD3A', '#F7F6ED']

// Fraction of screen height per second
const FALL_SPEED_MIN = 0.8
const FALL_SPEED_MAX = 0.9

// Size clamps (pixels) and relative bounds (fraction of smaller dimension)
const SIZE_MIN_CLAMP = 6
const SIZE_MAX_CLAMP = 20
const SIZE_MIN = 0.015
const SIZE_MAX = 0.04

interface ConfettiProps {
  /** Particles spawned per second */
  density?: number
  /** How long to keep spawning particles (ms) */
  duration?: number
  /** Delay before starting (ms) */
  delay?: number
}

interface ParticleData {
  id: number
  Shape: (typeof SHAPES)[number]
  color: string
  size: number
  initialRotation: number
  rotationSpeed: number
  opacity: number
  xPercent: number
  yOffset: number
  driftX: number
  swayAmplitude: number
  fallSpeed: number
  spawnDelay: number
}

const generateParticle = (id: number, spawnDelay: number): ParticleData => ({
  id,
  Shape: SHAPES[Math.floor(Math.random() * SHAPES.length)],
  color: COLORS[Math.floor(Math.random() * COLORS.length)],
  size: Math.random(),
  opacity: 0.8 + Math.random() * 0.2,
  initialRotation: Math.random() * 360,
  rotationSpeed: (Math.random() - 0.5) * 720,
  xPercent: Math.random(),
  yOffset: Math.random() * 0.05,
  driftX: (Math.random() - 0.5) * 100,
  swayAmplitude: 10 + Math.random() * 20,
  fallSpeed: FALL_SPEED_MIN + Math.random() * (FALL_SPEED_MAX - FALL_SPEED_MIN),
  spawnDelay,
})

const ConfettiParticle = memo(
  ({
    data,
    width,
    height,
  }: {
    data: ParticleData
    width: number
    height: number
  }) => {
    const {
      Shape,
      color,
      size: sizeRatio,
      initialRotation,
      rotationSpeed,
      opacity,
      xPercent,
      yOffset,
      driftX,
      swayAmplitude,
      fallSpeed,
      spawnDelay,
    } = data

    // Size scales with screen but is clamped to pixel bounds.
    // On large screens where both bounds hit the clamp, use full clamp range to preserve variation.
    const baseSize = Math.min(width, height)
    const rawMinSize = SIZE_MIN * baseSize
    const rawMaxSize = SIZE_MAX * baseSize
    const minSize = Math.max(SIZE_MIN_CLAMP, Math.min(rawMinSize, SIZE_MAX_CLAMP))
    const maxSize = Math.max(SIZE_MIN_CLAMP, Math.min(rawMaxSize, SIZE_MAX_CLAMP))
    const size =
      minSize < maxSize
        ? minSize + sizeRatio * (maxSize - minSize)
        : SIZE_MIN_CLAMP + sizeRatio * (SIZE_MAX_CLAMP - SIZE_MIN_CLAMP)

    const startX = xPercent * width
    const startY = -size - yOffset * height
    const endY = height + size
    const distance = endY - startY
    const actualFallSpeed = fallSpeed * height
    const fallDuration = (distance / actualFallSpeed) * 1000

    const progress = useRef(new Animated.Value(0)).current

    const animatedStyle = useMemo(() => {
      const translateY = progress.interpolate({
        inputRange: [0, 1],
        outputRange: [startY, endY],
      })

      // Combines linear drift with sinusoidal sway for natural falling motion
      const translateX = progress.interpolate({
        inputRange: [0, 0.25, 0.5, 0.75, 1],
        outputRange: [
          startX,
          startX + driftX * 0.25 + swayAmplitude,
          startX + driftX * 0.5,
          startX + driftX * 0.75 - swayAmplitude,
          startX + driftX,
        ],
      })

      const rotate = progress.interpolate({
        inputRange: [0, 1],
        outputRange: [
          `${initialRotation}deg`,
          `${initialRotation + rotationSpeed}deg`,
        ],
      })

      // Stays invisible until animation starts, fades in quickly, then gradually fades out
      const particleOpacity = progress.interpolate({
        inputRange: [0, 0.001, 0.05, 0.4, 0.7, 0.85, 1],
        outputRange: [0, 0, opacity, opacity * 0.8, opacity * 0.5, opacity * 0.2, 0],
      })

      const scale = progress.interpolate({
        inputRange: [0, 0.05, 0.95, 1],
        outputRange: [0, 1, 1, 0.3],
      })

      return {
        opacity: particleOpacity,
        transform: [{ translateX }, { translateY }, { rotate }, { scale }],
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
      const timeout = setTimeout(() => {
        Animated.timing(progress, {
          toValue: 1,
          duration: fallDuration,
          easing: Easing.linear,
          useNativeDriver: Platform.OS !== 'web',
        }).start()
      }, spawnDelay)

      return () => clearTimeout(timeout)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
      <Animated.View style={[styles.particle, animatedStyle]}>
        <Shape color={color} size={size} />
      </Animated.View>
    )
  },
)

ConfettiParticle.displayName = 'ConfettiParticle'

export const Confetti = memo(
  ({ density = 25, duration = 3000, delay = 0 }: ConfettiProps) => {
    const { width, height } = useWindowDimensions()

    // Generate ALL particles once with staggered spawn delays
    const particles = useMemo(() => {
      const totalParticles = Math.ceil((density * duration) / 1000)
      const intervalMs = 1000 / density

      return Array.from({ length: totalParticles }, (_, i) =>
        generateParticle(i, delay + i * intervalMs),
      )
    }, [density, duration, delay])

    return (
      <View style={styles.container} pointerEvents="none">
        {particles.map((data) => (
          <ConfettiParticle
            key={data.id}
            data={data}
            width={width}
            height={height}
          />
        ))}
      </View>
    )
  },
)

Confetti.displayName = 'Confetti'

const styles = StyleSheet.create({
  container: {
    ...StyleSheet.absoluteFillObject,
    overflow: 'hidden',
  },
  particle: {
    position: 'absolute',
    top: 0,
  },
})
